import { useState, useRef, useEffect } from "react";
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Menu, Bell, Search, LayoutDashboard, Settings, Users, Plus, ChevronDown, Filter, SortAsc, User, Group, MoreHorizontal, Moon, Sun } from "lucide-react";
import TableSection from './components/TableSection';

const TASK_GROUPS = [
  {
    name: "Priority",
    color: "border-green-600",
    tasks: [
      {
        item: "new Stripe account",
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Update Required", color: "bg-pink-500" },
        priority: { label: "Medium", color: "bg-blue-600" },
      },
    ],
  },
  {
    name: "In Progress",
    color: "border-blue-600",
    tasks: [
      {
        item: "Event Boost feature",
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "In Progress", color: "bg-green-600" },
        priority: { label: "Low", color: "bg-blue-400" },
      },
      {
        item: "Event Boost",
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Needs Revisions", color: "bg-orange-500" },
        priority: { label: "Medium", color: "bg-blue-600" },
      },
      {
        item: "Template #14",
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "In Review", color: "bg-blue-500" },
        priority: { label: "Low", color: "bg-blue-400" },
      },
    ],
  },
];

const NEXT_TASKS = [
  {
    item: 'Follow issue',
    developer: 'AZ',
    support: 'JD',
    requestedBy: 'EM',
    status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
    priority: { label: 'Low', color: 'bg-blue-400' }
  },
  {
    item: 'Set up database in AWS',
    developer: 'AZ',
    support: 'JD',
    requestedBy: 'EM',
    status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
    priority: { label: 'Medium', color: 'bg-blue-600' }
  },
  {
    item: 'ID.me data fetch',
    developer: 'JD',
    support: 'JD',
    requestedBy: 'EM',
    status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
    priority: { label: 'Low', color: 'bg-blue-400' }
  }
];

const AWS_TASKS = [
  {
    item: 'Move site to AWS',
    developer: '👤', // You can replace with initials or avatar logic
    support: '',
    requestedBy: '🧑', // Replace with actual initials/avatar if needed
    status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
    priority: { label: 'Medium', color: 'bg-blue-600' }
  }
];

const EVENT_PLATFORM_TASKS = [
  {
    item: 'Connecting each creator stripe...',
    developer: 'MS',
    support: '',
    requestedBy: '🧑',
    status: { label: 'Not Started', color: 'bg-[#5B4FFF]' },
    priority: { label: 'Low', color: 'bg-blue-400' }
  },
  {
    item: 'Second EVENT Approval with ...',
    developer: 'AZ',
    support: '',
    requestedBy: '🧑',
    status: { label: 'Not Started', color: 'bg-[#5B4FFF]' },
    priority: { label: 'Medium', color: 'bg-blue-600' }
  },
  {
    item: 'Brevo Template #30',
    developer: 'AZ',
    support: '',
    requestedBy: '👤',
    status: { label: 'Working on it', color: 'bg-orange-400' },
    priority: { label: 'Critical ⚠️', color: 'bg-pink-400' }
  }
];

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [personDropdownOpen, setPersonDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [groupByDropdownOpen, setGroupByDropdownOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [sidebarItem, setSidebarItem] = useState(null);
  const [hoveredItemIdx, setHoveredItemIdx] = useState(null);
  const [newItemText, setNewItemText] = useState("");
  const [taskGroups, setTaskGroups] = useState(TASK_GROUPS);
  const [nextTasks, setNextTasks] = useState([
    {
      item: 'Follow issue',
      developer: 'AZ',
      support: 'JD',
      requestedBy: 'EM',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Low', color: 'bg-blue-400' }
    },
    {
      item: 'Set up database in AWS',
      developer: 'AZ',
      support: 'JD',
      requestedBy: 'EM',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    },
    {
      item: 'ID.me data fetch',
      developer: 'JD',
      support: 'JD',
      requestedBy: 'EM',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Low', color: 'bg-blue-400' }
    }
  ]);
  const [awsTasks, setAwsTasks] = useState([
    {
      item: 'Move site to AWS',
      developer: '👤',
      support: '',
      requestedBy: '🧑',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    }
  ]);
  const [eventPlatformTasks, setEventPlatformTasks] = useState([
    {
      item: 'Connecting each creator stripe...',
      developer: 'MS',
      support: '',
      requestedBy: '🧑',
      status: { label: 'Not Started', color: 'bg-[#5B4FFF]' },
      priority: { label: 'Low', color: 'bg-blue-400' }
    },
    {
      item: 'Second EVENT Approval with ...',
      developer: 'AZ',
      support: '',
      requestedBy: '🧑',
      status: { label: 'Not Started', color: 'bg-[#5B4FFF]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    },
    {
      item: 'Brevo Template #30',
      developer: 'AZ',
      support: '',
      requestedBy: '👤',
      status: { label: 'Working on it', color: 'bg-orange-400' },
      priority: { label: 'Critical ⚠️', color: 'bg-pink-400' }
    }
  ]);
  const [onHoldTasks, setOnHoldTasks] = useState([
    {
      item: 'Podcaster check box',
      developer: 'AZ',
      support: 'JD',
      requestedBy: 'EM',
      status: { label: 'Not Started', color: 'bg-[#5B4FFF]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    },
    {
      item: 'Mobile update (trending section)',
      developer: 'AZ',
      support: '',
      requestedBy: 'EM, JD',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    },
    {
      item: 'Few spacing edits',
      developer: 'AZ',
      support: '',
      requestedBy: 'EM, JD',
      status: { label: 'ON HOLD', color: 'bg-[#4B3A36]' },
      priority: { label: 'Medium', color: 'bg-blue-600' }
    }
  ]);

  const [completedTasks, setCompletedTasks] = useState([
    {
      item: 'ID.me go LIVE',
      developer: 'AZ',
      support: <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="AZ" className="w-7 h-7 rounded-full" />,
      requestedBy: <img src="https://randomuser.me/api/portraits/men/44.jpg" alt="Requester" className="w-7 h-7 rounded-full" />,
      status: { label: 'Completed', color: 'bg-green-500' },
      priority: { label: 'Critical ⚠️', color: 'bg-pink-400' },
    },
    {
      item: '#1 RSS Fields still required',
      developer: '',
      support: '',
      requestedBy: '',
      status: { label: 'Completed', color: 'bg-green-500' },
      priority: { label: '', color: '' },
    },
    {
      item: 'Take off PDTV menu item',
      developer: 'AZ',
      support: '',
      requestedBy: <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Requester" className="w-7 h-7 rounded-full" />,
      status: { label: 'Completed', color: 'bg-green-500' },
      priority: { label: 'Critical ⚠️', color: 'bg-pink-400' },
    },
    {
      item: 'Move ID.me Subcategory',
      developer: 'AZ',
      support: '',
      requestedBy: <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Requester" className="w-7 h-7 rounded-full" />,
      status: { label: 'Completed', color: 'bg-green-500' },
      priority: { label: 'High', color: 'bg-red-400' },
    },
  ]);

  const popoverRef = useRef(null);
  const personDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const groupByDropdownRef = useRef(null);
  const notificationRef = useRef(null);

  // Add subitems state per main item row
  const [subitemsMap, setSubitemsMap] = useState({});
  const [addingSubitemFor, setAddingSubitemFor] = useState(null);
  const [newSubitemText, setNewSubitemText] = useState("");

  // Add state for sidebar tab
  const [sidebarTab, setSidebarTab] = useState('updates');

  // Add state for sidebar menu
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
  const [sidebarMenuAnchor, setSidebarMenuAnchor] = useState(null);
  const [sidebarEmailPrefOpen, setSidebarEmailPrefOpen] = useState(false);
  const sidebarMenuRef = useRef(null);
  const sidebarEmailPrefRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const sidebarOverlayRef = useRef(null);

  // Responsive check
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 640);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarOpen && isMobile) {
        if (sidebarOverlayRef.current && !sidebarOverlayRef.current.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    }
    if (sidebarOpen && isMobile) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen, isMobile]);

  // Ensure dark mode class is applied to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  // Add item handler
  const handleAddItem = (section) => {
    if (!newItemText.trim()) return;
    if (section === 'Priority' || section === 0) {
      const updatedGroups = [...taskGroups];
      updatedGroups[0].tasks.push({
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      });
      setTaskGroups(updatedGroups);
    } else if (section === 'In Progress' || section === 1) {
      const updatedGroups = [...taskGroups];
      updatedGroups[1].tasks.push({
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      });
      setTaskGroups(updatedGroups);
    } else if (section === 'Next') {
      setNextTasks([...nextTasks, {
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      }]);
    } else if (section === 'AWS') {
      setAwsTasks([...awsTasks, {
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      }]);
    } else if (section === 'Event Platform') {
      setEventPlatformTasks([...eventPlatformTasks, {
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      }]);
    } else if (section === 'On Hold') {
      setOnHoldTasks([...onHoldTasks, {
        item: newItemText,
        developer: "AZ",
        support: "JD",
        requestedBy: "EM",
        status: { label: "Working on it", color: "bg-orange-400" },
        priority: { label: "Medium", color: "bg-blue-600" },
      }]);
    }
    setNewItemText("");
  };

  const getSubitems = (rowKey) => subitemsMap[rowKey] || [
    { subitem: 'Add an "All"', owner: 'AZ', colab: 'AZ', status: 'Revise', priority: 'Medium', msgCount: 1 },
    { subitem: 'LinkedIn Image', owner: 'AZ', colab: 'JD', status: 'Revise', priority: 'Medium', msgCount: 1 },
  ];
  const handleAddSubitem = (rowKey) => {
    if (!newSubitemText.trim()) return;
    setSubitemsMap(prev => ({
      ...prev,
      [rowKey]: [...getSubitems(rowKey), { subitem: newSubitemText, owner: 'AZ', colab: 'JD', status: 'Revise', priority: 'Medium', msgCount: 1 }]
    }));
    setNewSubitemText("");
    setAddingSubitemFor(null);
  };

  return (
    <div className={`${darkMode ? "dark" : ""} min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white`}>
      <div>
        {/* Sidebar for desktop: always visible, fixed, no overlay, no animation */}
        <aside className="fixed left-0 top-0 z-30 h-screen bg-white dark:bg-gray-800 border-r w-64 flex flex-col hidden sm:flex">
          <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
            <span className="font-bold text-lg flex items-center gap-2">
              <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center">m</span>
              <span>monday</span>
            </span>
          </div>
          <nav className="flex-1 flex flex-col gap-1 p-2">
            <SidebarItem icon={<LayoutDashboard />} label="Home" open={true} active />
            <SidebarItem icon={<Users />} label="My work" open={true} />
            <SidebarItem icon={<MoreHorizontal />} label="More" open={true} />
            <div className="mt-4 text-xs text-gray-400 px-2">Favorites</div>
            <SidebarItem icon={<span className="bg-purple-600 w-4 h-4 rounded-full inline-block mr-2" />} label="Web Development" open={true} />
            <div className="mt-4 text-xs text-gray-400 px-2">Workspaces</div>
            <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Current Tasks" open={true} active />
            <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Event Boost" open={true} />
            <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Go High Level" open={true} />
            <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="ID.me" open={true} />
          </nav>
        </aside>
        {/* Sidebar for mobile: drawer with overlay */}
        {isMobile && sidebarOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black bg-opacity-40 sm:hidden" />
            <aside
              ref={sidebarOverlayRef}
              className="fixed top-0 left-0 z-50 h-screen bg-white dark:bg-gray-800 border-r w-64 flex flex-col sm:hidden transition-transform duration-300 translate-x-0"
            >
              <div className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                <span className="font-bold text-lg flex items-center gap-2">
                  <span className="bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center">m</span>
                  <span>monday</span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="sm:hidden"
                  aria-label="Close sidebar"
                >
                  <span className="text-2xl">&times;</span>
                </Button>
              </div>
              <nav className="flex-1 flex flex-col gap-1 p-2">
                <SidebarItem icon={<LayoutDashboard />} label="Home" open={sidebarOpen} active />
                <SidebarItem icon={<Users />} label="My work" open={sidebarOpen} />
                <SidebarItem icon={<MoreHorizontal />} label="More" open={sidebarOpen} />
                <div className="mt-4 text-xs text-gray-400 px-2">Favorites</div>
                <SidebarItem icon={<span className="bg-purple-600 w-4 h-4 rounded-full inline-block mr-2" />} label="Web Development" open={sidebarOpen} />
                <div className="mt-4 text-xs text-gray-400 px-2">Workspaces</div>
                <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Current Tasks" open={sidebarOpen} active />
                <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Event Boost" open={sidebarOpen} />
                <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="Go High Level" open={sidebarOpen} />
                <SidebarItem icon={<span className="bg-gray-300 w-4 h-4 rounded-full inline-block mr-2" />} label="ID.me" open={sidebarOpen} />
              </nav>
            </aside>
          </>
        )}
        {/* Main Content */}
        <main
          className={`transition-all duration-300
            sm:ml-64
            h-screen overflow-y-auto flex flex-col
          `}
        >
          {/* Top Bar */}
          <div className="flex flex-row items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-white dark:bg-gray-800 shadow border-b dark:border-gray-700 relative">
            {/* Mobile: Only show dark mode and notification */}
            <div className="flex flex-row items-center gap-2 w-full sm:hidden">
              {/* Hamburger menu for mobile (optional, remove if you want to hide) */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="sm:hidden"
                aria-label="Open sidebar"
              >
                <Menu className="w-6 h-6" />
              </Button>
              {/* Spacer to push icons to right */}
              <div className="flex-1" />
              {/* Dark mode toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDarkMode(!darkMode)}
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
              </Button>
              {/* Notification icon */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setNotificationOpen((v) => !v)}
                aria-label="Notifications"
              >
                <Bell className="w-6 h-6" />
              </Button>
            </div>
            {/* Desktop: Show full header */}
            <div className="hidden sm:flex flex-row items-center justify-between w-full">
              <div className="flex items-center gap-2 relative">
                {/* Board Title with Popover */}
                <button
                  className="text-2xl font-bold flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring"
                  onClick={() => setPopoverOpen((v) => !v)}
                >
                  Current Tasks
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                </button>
                {popoverOpen && (
                  <div
                    ref={popoverRef}
                    className="absolute left-0 top-14 z-30 w-[380px] max-w-full bg-white shadow-lg rounded-2xl p-8 border border-gray-200 dark:bg-gray-900 dark:border-gray-700"
                    style={{ minWidth: 320 }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-bold text-xl">Current Tasks</span>
                      <span className="text-gray-400 cursor-pointer">★</span>
                    </div>
                    <div className="text-base text-gray-500 mb-4">In order of priority</div>
                    <hr className="my-4 border-gray-200 dark:border-gray-700" />
                    <div className="text-xs text-gray-500 font-semibold mb-2">Board info</div>
                    <div className="flex items-center mb-3 ">
                      <span className="text-sm text-gray-500 mr-3">Board type</span>
                      <span className="flex items-center gap-1 text-sm font-medium bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded">
                        <LayoutDashboard className="w-4 h-4" /> Main <ChevronDown className="w-3 h-3" />
                      </span>
                    </div>
                    <div className="flex items-center mb-3">
                      <span className="text-sm text-gray-500 mr-6">Owners</span>
                      <span className="flex -space-x-2">
                        <Avatar initials="AB" />
                        <Avatar initials="CD" />
                      </span>
                    </div>
                    <div className="flex items-center mb-3">
                      <span className="text-sm text-gray-500 mr-3">Created by</span>
                      <span className="flex items-center gap-2"><Avatar initials="AB" /> <span className="text-sm text-gray-500">on Jan 3, 2025</span></span>
                    </div>
                    <div className="flex items-center">
                      <Bell className="w-4 h-4 text-gray-400 " />
                      <span className="text-sm text-gray-500 mr-3">Notifications</span>
                      <span className="ml-2 text-sm font-medium">Everything</span>
                    </div>
                  </div>
                )}
                {/* ...rest of top bar controls... */}
                <Button variant="outline" size="sm" className="ml-4 flex items-center gap-1"><Plus className="w-4 h-4" /> New item</Button>
                <div className="ml-4 flex items-center gap-2 relative">
                  {/* Search popover */}
                  <button
                    className="flex items-center gap-1 focus:outline-none"
                    onClick={() => {
                      setSearchOpen((v) => !v);
                      setPersonDropdownOpen(false);
                    }}
                  >
                    <Search className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    <span className="text-sm text-gray-500">Search</span>
                  </button>
                  {searchOpen && (
                    <div className="absolute left-0 top-8 z-30 w-64 bg-white dark:bg-gray-900 shadow-xl rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex items-center gap-2">
                      <Search className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                      <input type="text" placeholder="Search..." className="flex-1 outline-none px-2 py-1 border rounded-md bg-transparent dark:border-gray-700" autoFocus />
                    </div>
                  )}
                  {/* Person popover */}
                  <button
                    className="flex items-center gap-1 ml-4 focus:outline-none"
                    onClick={() => {
                      setPersonDropdownOpen((v) => !v);
                      setSearchOpen(false);
                    }}
                  >
                    <User className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    <span className="text-sm text-gray-500">Person</span>
                  </button>
                  {personDropdownOpen && (
                    <div ref={personDropdownRef} className="absolute left-32 top-8 z-30 w-96 bg-white dark:bg-gray-900 shadow-xl rounded-xl p-5 border border-gray-100 dark:border-gray-700">
                      <div className="font-semibold mb-1">Filter this board by person</div>
                      <div className="flex items-center mb-2">
                        <input type="text" className="flex-1 px-2 py-1 border rounded-md bg-transparent dark:border-gray-700 text-sm" placeholder="Type a name..." disabled />
                        <button className="ml-2 px-3 py-1 rounded bg-gray-200 text-gray-400 text-xs font-semibold cursor-not-allowed" disabled>Save as new view</button>
                      </div>
                      <div className="text-sm text-gray-500 mb-2">And find items they're working on.</div>
                      <div className="flex gap-3">
                        <Avatar initials={<img src="https://randomuser.me/api/portraits/men/32.jpg" alt="AZ" className="w-7 h-7 rounded-full" />} />
                        <Avatar initials="AZ" />
                        <Avatar initials={<img src="https://randomuser.me/api/portraits/women/44.jpg" alt="CD" className="w-7 h-7 rounded-full" />} />
                        <Avatar initials="MS" />
                      </div>
                    </div>
                  )}
                  {/* Filter popover */}
                  <button
                    className="flex items-center gap-1 ml-4 focus:outline-none"
                    onClick={() => {
                      setFilterDropdownOpen((v) => !v);
                      setSearchOpen(false);
                      setPersonDropdownOpen(false);
                    }}
                  >
                    <Filter className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    <span className="text-sm text-gray-500">Filter</span>
                  </button>
                  {filterDropdownOpen && (
                    <div ref={filterDropdownRef} className="absolute left-0 top-8 z-30 w-[36rem] bg-white dark:bg-gray-900 shadow-xl rounded-xl p-5 border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-base">Advanced filters <span className="text-xs text-gray-400 ml-2">Showing all of 288 items</span></span>
                        <div className="flex items-center gap-2">
                          <button className="text-xs text-gray-500">Clear all</button>
                          <button className="px-3 py-1 rounded bg-gray-200 text-gray-400 text-xs font-semibold cursor-not-allowed" disabled>Save as new view</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-sm text-gray-500">Where</span>
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700">
                          <option>Column</option>
                        </select>
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700">
                          <option>Condition</option>
                        </select>
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700">
                          <option>Value</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-4 mb-4">
                        <button className="text-xs text-blue-600 font-semibold">+ New filter</button>
                        <button className="text-xs text-blue-600 font-semibold">+ New group</button>
                      </div>
                      <div className="flex items-center justify-between border-t pt-4 mt-4">
                        <button className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"><span className="inline-block w-4 h-4 bg-gradient-to-tr from-blue-400 to-pink-400 rounded-full mr-1"></span>Filter with AI</button>
                        <button className="text-xs text-blue-600 font-semibold">Switch to quick filters</button>
                      </div>
                    </div>
                  )}
                  {/* Sort popover */}
                  <button
                    className="flex items-center gap-1 ml-4 focus:outline-none"
                    onClick={() => {
                      setSortDropdownOpen((v) => !v);
                      setSearchOpen(false);
                      setPersonDropdownOpen(false);
                      setFilterDropdownOpen(false);
                    }}
                  >
                    <SortAsc className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    <span className="text-sm text-gray-500">Sort</span>
                  </button>
                  {sortDropdownOpen && (
                    <div ref={sortDropdownRef} className="absolute left-0 top-8 z-30 w-[32rem] bg-white dark:bg-gray-900 shadow-xl rounded-xl p-5 border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1 font-semibold text-base">Sort by <span className="text-gray-400 cursor-pointer" title="Sort your board"><svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><circle cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='2' fill='none' /><text x='12' y='16' textAnchor='middle' fontSize='12' fill='currentColor'>?</text></svg></span></div>
                        <button className="px-3 py-1 rounded bg-gray-200 text-gray-400 text-xs font-semibold cursor-not-allowed" disabled>Save as new view</button>
                      </div>
                      <div className="flex items-center gap-2 mb-4">
                        <button className="w-8 h-8 flex items-center justify-center text-gray-400 cursor-move"><svg xmlns='http://www.w3.org/2000/svg' className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><circle cx='12' cy='12' r='1.5' fill='currentColor' /><circle cx='12' cy='6' r='1.5' fill='currentColor' /><circle cx='12' cy='18' r='1.5' fill='currentColor' /></svg></button>
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700 flex-1">
                          <option>Choose column</option>
                        </select>
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700">
                          <option>Ascending</option>
                          <option>Descending</option>
                        </select>
                      </div>
                      <button className="text-xs text-blue-600 font-semibold mb-2">+ New sort</button>
                    </div>
                  )}
                  {/* Group by popover */}
                  <button
                    className="flex items-center gap-1 ml-4 focus:outline-none"
                    onClick={() => {
                      setGroupByDropdownOpen((v) => !v);
                      setSearchOpen(false);
                      setPersonDropdownOpen(false);
                      setFilterDropdownOpen(false);
                      setSortDropdownOpen(false);
                    }}
                  >
                    <Group className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                    <span className="text-sm text-gray-500">Group by</span>
                  </button>
                  {groupByDropdownOpen && (
                    <div ref={groupByDropdownRef} className="absolute left-0 top-8 z-30 w-[32rem] bg-white dark:bg-gray-900 shadow-xl rounded-xl p-5 border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1 font-semibold text-base">Group items by <span className="text-gray-400 cursor-pointer" title="Group your board"><svg xmlns='http://www.w3.org/2000/svg' className='inline w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'><circle cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='2' fill='none' /><text x='12' y='16' textAnchor='middle' fontSize='12' fill='currentColor'>?</text></svg></span></div>
                        <div className="flex items-center gap-2">
                          <button className="text-xs text-gray-500">Clear</button>
                          <button className="px-3 py-1 rounded bg-gray-200 text-gray-400 text-xs font-semibold cursor-not-allowed" disabled>Save as new view</button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-4">
                        <select className="border rounded px-2 py-1 text-sm bg-transparent dark:border-gray-700 flex-1">
                          <option>Select or search column name</option>
                        </select>
                        <select className="border rounded px-2 py-1 text-sm bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 flex-1" disabled>
                          <option>Select sorting option</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2 mb-4">
                        <input type="checkbox" id="show-empty-groups" className="mr-2" disabled />
                        <label htmlFor="show-empty-groups" className="text-sm text-gray-500">Show empty groups</label>
                      </div>
                      <div className="border-t pt-4 mt-4">
                        <button className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-gray-300 inline-block"></span>Give feedback</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* ...rest of top bar... */}
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => setDarkMode(!darkMode)}>
                  {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
                <div className="relative">
                  <button onClick={() => setNotificationOpen((v) => !v)} className="focus:outline-none">
                    <Bell className="w-5 h-5 text-gray-500 dark:text-gray-300 relative" />
                  </button>
                  {notificationOpen && (
                    <div ref={notificationRef} className="absolute right-0 mt-3 w-[480px] max-w-[95vw] bg-white dark:bg-gray-900 shadow-2xl rounded-2xl p-0 border border-gray-200 dark:border-gray-700 z-50">
                      <div className="flex items-center px-6 pt-6 pb-2 border-b border-gray-100 dark:border-gray-800">
                        <div className="flex gap-2">
                          <button onClick={() => setNotificationTab('all')} className={`px-4 py-2 rounded-lg font-medium focus:outline-none ${notificationTab === 'all' ? 'text-blue-600 bg-blue-50 border border-blue-200' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>All</button>
                          <button onClick={() => setNotificationTab('mentioned')} className={`px-4 py-2 rounded-lg font-medium focus:outline-none ${notificationTab === 'mentioned' ? 'text-blue-600 bg-blue-50 border border-blue-200' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>Mentioned</button>
                          <button onClick={() => setNotificationTab('assigned')} className={`px-4 py-2 rounded-lg font-medium focus:outline-none ${notificationTab === 'assigned' ? 'text-blue-600 bg-blue-50 border border-blue-200' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>Assigned to me</button>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          <button className="text-gray-400 hover:text-gray-600"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></button>
                          <button className="text-gray-400 hover:text-gray-600"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="currentColor" /></svg></button>
                        </div>
                      </div>
                      <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-800">
                        <input type="text" placeholder="Search all notifications" className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none" />
                        <label className="flex items-center mt-2 text-sm gap-2">
                          <input type="checkbox" className="form-checkbox" /> Unread only
                        </label>
                      </div>
                      <div className="px-6 py-4 bg-blue-50 flex items-center gap-4 border-b border-gray-100 dark:border-gray-800">
                        <img src="https://cdn.monday.com/images/logos/slack-logo.svg" alt="Slack" className="w-10 h-10 rounded-full bg-white p-1 border border-gray-200" />
                        <span className="flex-1 text-gray-700 text-sm">Get your monday.com notifications directly on Slack</span>
                        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold">Connect</button>
                        <button className="ml-2 text-gray-400">✕</button>
                      </div>
                      {/* Notification Content by Tab */}
                      {notificationTab === 'all' && (
                        <>
                          <div className="px-6 py-2 text-xs text-gray-400 font-semibold">Yesterday</div>
                          <div className="px-6 pb-2">
                            <div className="bg-blue-50 rounded-xl p-4 mb-2">
                              <div className="flex items-center gap-2 mb-1">
                                <Avatar initials={<img src='https://randomuser.me/api/portraits/men/32.jpg' alt='Andrew' className='w-7 h-7 rounded-full' />} />
                                <span className="font-semibold text-gray-800 dark:text-gray-100">Andrew</span>
                                <span className="text-xs text-gray-400 ml-2">Wrote an update on an item you're subscribed to</span>
                                <span className="ml-auto text-xs text-gray-400">1 day</span>
                                <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
                              </div>
                              <div className="text-sm text-gray-700 dark:text-gray-200 mb-1">@Aleem Zada here is the documentation for customer portal:<br />https://docs.stripe.com/customer-management/integrate-customer-portal...</div>
                              <div className="flex items-center gap-2 text-xs text-blue-700 mt-1">
                                <span className="inline-flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#635bff" /><circle cx="12" cy="12" r="4" fill="#fff" /></svg>new Stripe account</span>
                              </div>
                            </div>
                          </div>
                          <div className="px-6 py-2 text-xs text-gray-400 font-semibold">Last 7 Days</div>
                          <div className="px-6 pb-4">
                            <div className="bg-blue-50 rounded-xl p-4 mb-2">
                              <div className="flex items-center gap-2 mb-1">
                                <Avatar initials={<img src='https://randomuser.me/api/portraits/men/32.jpg' alt='Andrew' className='w-7 h-7 rounded-full' />} />
                                <span className="font-semibold text-gray-800 dark:text-gray-100">Andrew</span>
                                <span className="text-xs text-gray-400 ml-2">Assigned you to the item "Login workaround"</span>
                                <span className="ml-auto text-xs text-gray-400">2 days</span>
                                <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-blue-700 mt-1">
                                <span className="inline-flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#635bff" /><circle cx="12" cy="12" r="4" fill="#fff" /></svg>Login workaround</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      {notificationTab === 'mentioned' && (
                        <div className="px-6 py-8 text-center text-gray-400 text-sm">No mentioned notifications.</div>
                      )}
                      {notificationTab === 'assigned' && (
                        <div className="px-6 py-8 text-center text-gray-400 text-sm">No assigned notifications.</div>
                      )}
                    </div>
                  )}
                </div>
                <span className="w-8 h-8 bg-gray-300 dark:bg-gray-700 rounded-full flex items-center justify-center font-bold">AZ</span>
              </div>
            </div>
          </div>
          {/* Board/Table */}
          <div className="p-2 sm:p-4 md:p-6 flex flex-col gap-4">
            {/* Priority Section */}
            <TableSection
              title="Priority"
              borderColor="border-green-600"
              tasks={taskGroups.find(g => g.name === 'Priority')?.tasks || []}
              getSubitems={idx => getSubitems(`0-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`0-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('Priority')}
              onReorder={reordered => setTaskGroups(prev => prev.map(g => g.name === 'Priority' ? { ...g, tasks: reordered } : g))}
            />
            {/* In Progress Section */}
            <TableSection
              title="In Progress"
              borderColor="border-blue-600"
              tasks={taskGroups.find(g => g.name === 'In Progress')?.tasks || []}
              getSubitems={idx => getSubitems(`1-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`1-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('In Progress')}
              onReorder={reordered => setTaskGroups(prev => prev.map(g => g.name === 'In Progress' ? { ...g, tasks: reordered } : g))}
            />
            {/* Next Section (after In Progress) */}
            <TableSection
              title="Next"
              borderColor="border-yellow-400"
              tasks={nextTasks}
              getSubitems={idx => getSubitems(`next-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`next-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('Next')}
              onReorder={setNextTasks}
            />
            {/* AWS Section (after Next) */}
            <TableSection
              title="AWS"
              borderColor="border-purple-400"
              tasks={awsTasks}
              getSubitems={idx => getSubitems(`aws-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`aws-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('AWS')}
              onReorder={setAwsTasks}
            />
            {/* Event Platform Section (after AWS) */}
            <TableSection
              title="Event Platform"
              borderColor="border-purple-500"
              tasks={eventPlatformTasks}
              getSubitems={idx => getSubitems(`eventplatform-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`eventplatform-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('Event Platform')}
              onReorder={setEventPlatformTasks}
            />
            {/* On Hold Section (after Event Platform) */}
            <TableSection
              title="On Hold"
              borderColor="border-gray-400"
              tasks={onHoldTasks}
              getSubitems={idx => getSubitems(`onhold-${idx}`)}
              onAddSubitem={idx => handleAddSubitem(`onhold-${idx}`)}
              newSubitemText={newSubitemText}
              setNewSubitemText={setNewSubitemText}
              addingSubitemFor={addingSubitemFor}
              setAddingSubitemFor={setAddingSubitemFor}
              newItemText={newItemText}
              setNewItemText={setNewItemText}
              handleAddItem={() => handleAddItem('On Hold')}
              onReorder={setOnHoldTasks}
            />

<TableSection
  title="Completed"
  borderColor="border-green-700"
  tasks={completedTasks}
  getSubitems={idx => completedSubitems[idx] || []}
  onAddSubitem={null}
  newSubitemText={''}
  setNewSubitemText={() => {}}
  addingSubitemFor={null}
  setAddingSubitemFor={() => {}}
  newItemText={''}
  setNewItemText={() => {}}
  handleAddItem={null}
  onReorder={setCompletedTasks}
/>
          </div> {/* Close p-6 */}
        </main> {/* Close main */}
        {/* Sidebar modal */}
        {sidebarItem && (
          <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-end">
            <div className="w-[900px] max-w-full h-full bg-white dark:bg-gray-900 shadow-2xl p-0 relative flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
                <div className="text-2xl font-bold">{sidebarItem.item}</div>
                <div className="flex items-center gap-2 relative">
                  {/* Avatars */}
                  <span className="flex -space-x-2 mr-2">
                    <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Andrew" className="w-8 h-8 rounded-full border-2 border-white" />
                    <span className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold border-2 border-white">AZ</span>
                    <img src="https://randomuser.me/api/portraits/men/44.jpg" alt="JD" className="w-8 h-8 rounded-full border-2 border-white" />
                  </span>
                  {/* 3-dots menu button */}
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 text-2xl relative"
                    onClick={e => {
                      setSidebarMenuOpen(v => !v);
                      setSidebarMenuAnchor(e.currentTarget);
                    }}
                    aria-label="More options"
                    type="button"
                  >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="5" cy="12" r="2" fill="#888" /><circle cx="12" cy="12" r="2" fill="#888" /><circle cx="19" cy="12" r="2" fill="#888" /></svg>
                  </button>
                  {/* Popup menu */}
                  {sidebarMenuOpen && (
                    <div ref={sidebarMenuRef} className="absolute right-0 top-12 z-50 w-64 bg-white shadow-xl rounded-xl border border-gray-200 py-2 text-sm">
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M16 12v4a4 4 0 01-8 0v-4" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><rect x="8" y="4" width="8" height="4" rx="2" stroke="#888" strokeWidth="2" /></svg>
                        Manage subscribers
                      </button>
                      <div
                        className="relative group"
                        onMouseEnter={() => setSidebarEmailPrefOpen(true)}
                        onMouseLeave={() => setSidebarEmailPrefOpen(false)}
                      >
                        <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 justify-between">
                          <span className="flex items-center gap-2">
                            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#888" strokeWidth="2" /><path d="M3 7l9 6 9-6" stroke="#888" strokeWidth="2" /></svg>
                            Email preferences
                          </span>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                        {/* Email preferences submenu */}
                        {sidebarEmailPrefOpen && (
                          <div ref={sidebarEmailPrefRef} className="absolute right-full top-0 -mr-2 w-48 bg-white shadow-xl rounded-xl border border-gray-200 py-2 z-50">
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                              <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#888" strokeWidth="2" /><path d="M3 7l9 6 9-6" stroke="#888" strokeWidth="2" /></svg>
                              Instant email
                              <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                              <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#888" strokeWidth="2" /><path d="M3 7l9 6 9-6" stroke="#888" strokeWidth="2" /></svg>
                              No email
                            </button>
                          </div>
                        )}
                      </div>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" stroke="#888" strokeWidth="2" /><path d="M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" /></svg>
                        Export updates to Excel
                      </button>
                      <button className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M17 16l4-4m0 0l-4-4m4 4H7" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><rect x="3" y="4" width="18" height="16" rx="2" stroke="#888" strokeWidth="2" /></svg>
                        Copy item link
                      </button>
                      <button className="w-full text-left px-4 py-2 flex items-center gap-2 text-gray-300 cursor-not-allowed" disabled>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="6" y="4" width="12" height="16" rx="2" stroke="#bbb" strokeWidth="2" /><path d="M9 12h6" stroke="#bbb" strokeWidth="2" strokeLinecap="round" /></svg>
                        Archive
                      </button>
                      <button className="w-full text-left px-4 py-2 flex items-center gap-2 text-gray-300 cursor-not-allowed" disabled>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="6" y="4" width="12" height="16" rx="2" stroke="#bbb" strokeWidth="2" /><path d="M9 12h6" stroke="#bbb" strokeWidth="2" strokeLinecap="round" /></svg>
                        Delete
                      </button>
                    </div>
                  )}
                  <button className="text-2xl text-gray-400 hover:text-gray-700 ml-2" onClick={() => setSidebarItem(null)}>&times;</button>
                </div>
              </div>
              {/* Tabs */}
              <div className="flex items-center gap-6 px-6 pt-4 border-b border-gray-200">
                <button onClick={() => setSidebarTab('updates')} className={`pb-3 font-medium flex items-center gap-1 ${sidebarTab === 'updates' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Updates <span className="text-xs bg-blue-100 text-blue-600 rounded px-2">5</span></button>
                <button onClick={() => setSidebarTab('files')} className={`pb-3 font-medium ${sidebarTab === 'files' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Files</button>
                <button onClick={() => setSidebarTab('activity')} className={`pb-3 font-medium ${sidebarTab === 'activity' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Activity Log</button>
                <button className="pb-3 text-gray-500 font-medium text-xl">+</button>
              </div>
              {/* Tab Content */}
              {sidebarTab === 'updates' && (
                <>
                  {/* Update input */}
                  <div className="px-6 pt-6 pb-2 border-b border-gray-100">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 mb-2">
                      <input type="text" className="w-full bg-transparent outline-none text-base" placeholder="Write an update and mention others with @" />
                      <div className="flex items-center gap-4 mt-3">
                        <button className="flex items-center gap-1 text-gray-500 text-sm"><span>@</span> Mention</button>
                        <button className="flex items-center gap-1 text-gray-500 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M21 15V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h4l3 3 3-3h4a2 2 0 002-2z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> Attach</button>
                        <button className="flex items-center gap-1 text-gray-500 text-sm">GIF</button>
                        <button className="flex items-center gap-1 text-gray-500 text-sm"><span role="img" aria-label="smile">😊</span></button>
                        <span className="ml-auto flex gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M21 10.5V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h7" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> Update via email</span>
                          <span className="flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2" /><path d="M8 12h8" stroke="#888" strokeWidth="2" strokeLinecap="round" /></svg> Give feedback</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Updates list */}
                  <div className="px-6 py-4 flex-1 overflow-y-auto">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4">
                      <div className="flex items-center gap-3 mb-1">
                        <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Andrew" className="w-9 h-9 rounded-full" />
                        <span className="font-bold text-gray-800">Andrew</span>
                        <span className="text-xs text-gray-400">22d</span>
                        <span className="ml-auto flex gap-2">
                          <span className="text-xl text-gray-300">✨</span>
                          <span className="text-xl text-gray-300">🔔</span>
                          <span className="text-xl text-gray-300">⋯</span>
                        </span>
                      </div>
                      <div className="mb-2"><span className="text-blue-600 font-medium cursor-pointer">@Aleem Zada</span> we can work on this next to finish.</div>
                      <div className="flex items-center gap-6 text-xs text-gray-400 border-t pt-2 mt-2">
                        <span className="flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> Like</span>
                        <span className="flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M3 10h18M3 6h18M3 14h18M3 18h18" stroke="#888" strokeWidth="2" strokeLinecap="round" /></svg> Reply</span>
                        <span className="ml-auto flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="#888" /></svg> 2</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {sidebarTab === 'files' && (
                <div className="flex-1 flex flex-col px-6 py-6">
                  {/* Top bar */}
                  <div className="flex items-center gap-3 mb-8">
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 font-medium bg-white hover:bg-gray-50">
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="4" y="11" width="16" height="2" rx="1" fill="#888" /><rect x="11" y="4" width="2" height="16" rx="1" fill="#888" /></svg>
                      Add file
                    </button>
                    <input type="text" className="flex-1 px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 outline-none" placeholder="Search for files" />
                    <div className="flex items-center gap-2 ml-2">
                      <button className="w-10 h-10 flex items-center justify-center rounded border border-gray-200 bg-blue-50 text-blue-600"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="6" height="6" rx="1" fill="#2563eb" /><rect x="14" y="4" width="6" height="6" rx="1" fill="#2563eb" /><rect x="4" y="14" width="6" height="6" rx="1" fill="#2563eb" /><rect x="14" y="14" width="6" height="6" rx="1" fill="#2563eb" /></svg></button>
                      <button className="w-10 h-10 flex items-center justify-center rounded border border-gray-200 bg-white text-gray-600"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="4" rx="1" fill="#888" /><rect x="4" y="10" width="16" height="4" rx="1" fill="#888" /><rect x="4" y="16" width="16" height="4" rx="1" fill="#888" /></svg></button>
                      <button className="w-10 h-10 flex items-center justify-center rounded border border-gray-200 bg-white text-gray-600"><svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M7 17v2a2 2 0 002 2h6a2 2 0 002-2v-2" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 12v6" stroke="#888" strokeWidth="2" strokeLinecap="round" /><path d="M16 16l-4 4-4-4" stroke="#888" strokeWidth="2" strokeLinecap="round" /></svg></button>
                    </div>
                  </div>
                  {/* Empty state illustration */}
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <svg width="220" height="140" viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="20" y="90" width="60" height="40" rx="8" fill="#E0E7FF" />
                      <rect x="100" y="90" width="40" height="40" rx="8" fill="#FEF08A" />
                      <rect x="160" y="90" width="40" height="40" rx="8" fill="#F1F5F9" />
                      <rect x="30" y="100" width="40" height="20" rx="4" fill="#fff" />
                      <rect x="110" y="100" width="20" height="20" rx="4" fill="#fff" />
                      <rect x="170" y="100" width="20" height="20" rx="4" fill="#fff" />
                      <circle cx="50" cy="110" r="8" fill="#22C55E" />
                      <rect x="110" y="110" width="12" height="8" rx="2" fill="#2563eb" />
                      <polygon points="120,120 130,120 125,130" fill="#2563eb" />
                      <rect x="170" y="110" width="12" height="8" rx="2" fill="#FACC15" />
                      <polygon points="180,120 190,120 185,130" fill="#FACC15" />
                      <rect x="60" y="30" width="100" height="60" rx="12" fill="#fff" stroke="#E0E7FF" strokeWidth="2" />
                      <rect x="80" y="50" width="20" height="20" rx="4" fill="#F1F5F9" />
                      <rect x="120" y="50" width="20" height="20" rx="4" fill="#FEF08A" />
                      <rect x="100" y="70" width="20" height="10" rx="2" fill="#E0E7FF" />
                      <rect x="140" y="70" width="10" height="10" rx="2" fill="#22C55E" />
                      <rect x="150" y="70" width="10" height="10" rx="2" fill="#2563eb" />
                      <rect x="160" y="70" width="10" height="10" rx="2" fill="#FACC15" />
                      <path d="M180 120c10-10 20-20 30-10" stroke="#F1F5F9" strokeWidth="4" strokeLinecap="round" />
                      <path d="M40 120c-10-10-20-20-30-10" stroke="#E0E7FF" strokeWidth="4" strokeLinecap="round" />
                      <rect x="140" y="30" width="40" height="20" rx="4" fill="#E0E7FF" />
                      <rect x="40" y="30" width="40" height="20" rx="4" fill="#F1F5F9" />
                      <rect x="90" y="10" width="40" height="20" rx="4" fill="#FEF08A" />
                      <rect x="110" y="20" width="20" height="10" rx="2" fill="#fff" />
                      <rect x="120" y="10" width="10" height="10" rx="2" fill="#2563eb" />
                      <rect x="130" y="10" width="10" height="10" rx="2" fill="#FACC15" />
                      <rect x="100" y="20" width="10" height="10" rx="2" fill="#22C55E" />
                      <rect x="110" y="10" width="10" height="10" rx="2" fill="#F1F5F9" />
                      <rect x="80" y="20" width="10" height="10" rx="2" fill="#E0E7FF" />
                      <rect x="90" y="20" width="10" height="10" rx="2" fill="#FACC15" />
                      <rect x="100" y="10" width="10" height="10" rx="2" fill="#F1F5F9" />
                      <rect x="110" y="10" width="10" height="10" rx="2" fill="#2563eb" />
                    </svg>
                  </div>
                </div>
              )}
              {sidebarTab === 'activity' && (
                <div className="flex-1 flex flex-col px-6 py-6">
                  {/* Activity list, table-like layout with fixed status columns */}
                  <div className="flex flex-col w-full">
                    {/* Table header (optional, can be commented out if not needed) */}
                    {/*
                    <div className="flex items-center text-xs text-gray-400 font-semibold border-b pb-2">
                      <div className="w-20">Time</div>
                      <div className="w-12">Avatar</div>
                      <div className="flex-1">Title & Path</div>
                      <div className="w-80 flex gap-4 justify-end">Status</div>
                    </div>
                    */}
                    {[
                      {
                        time: '4d',
                        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                        item: 'Event Boost feature',
                        path: [
                          { icon: <svg width='20' height='20' fill='none' viewBox='0 0 24 24'><rect x='4' y='8' width='16' height='8' rx='2' fill='#888' /></svg>, label: 'Subitems' },
                        
                        ],
                        status: [] // No status badges for first row
                      },
                      {
                        time: '21d',
                        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                        item: 'Event Boost feature',
                        path: [
                          { icon: <svg width='20' height='20' fill='none' viewBox='0 0 24 24'><rect x='4' y='8' width='16' height='8' rx='2' fill='#888' /></svg>, label: 'Status' }
                        ],
                        status: [
                          { label: 'Pending', color: 'bg-blue-700' },
                          { label: 'In Progress', color: 'bg-green-600' }
                        ]
                      },
                      {
                        time: '21d',
                        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                        item: 'Event Boost feature',
                        path: [
                          { icon: <svg width='20' height='20' fill='none' viewBox='0 0 24 24'><rect x='4' y='8' width='16' height='8' rx='2' fill='#888' /></svg>, label: 'Status' }
                        ],
                        status: [
                          { label: 'Needs Review', color: 'bg-orange-400' },
                          { label: 'Pending', color: 'bg-blue-700' }
                        ]
                      },
                      {
                        time: '21d',
                        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                        item: 'Event Boost feature',
                        path: [
                          { icon: <svg width='20' height='20' fill='none' viewBox='0 0 24 24'><rect x='4' y='8' width='16' height='8' rx='2' fill='#888' /></svg>, label: 'Status' }
                        ],
                        status: [
                          { label: 'Working on it', color: 'bg-yellow-400' },
                          { label: 'Needs Review', color: 'bg-orange-400' }
                        ]
                      }
                    ].map((log, idx) => {
                      let leftStatus = log.status[0];
                      let rightStatus = log.status[1];
                      if (log.status.length === 2) {
                        // No change needed, order is as per image
                      }
                      return (
                        <div key={idx} className="flex items-center py-6 border-b last:border-b-0 w-full">
                          <div className="w-20 flex items-center text-gray-400 text-sm">
                            <svg width='18' height='18' fill='none' viewBox='0 0 24 24'><circle cx='12' cy='12' r='10' stroke='#888' strokeWidth='2' /><path d='M12 6v6l4 2' stroke='#888' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' /></svg>
                            <span className="ml-1">{log.time}</span>
                          </div>
                          <div className="w-12 flex-shrink-0">
                            <img src={log.avatar} alt="avatar" className="w-10 h-10 rounded-full" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-800">{log.item}</span>
                            {log.path && log.path.length > 0 && (
                              <span className="flex items-center gap-1 text-gray-500 ml-2 truncate">
                                {log.path.map((p, i) => (
                                  <span key={i} className="flex items-center gap-1">
                                    {p.icon && <span className="mr-1">{p.icon}</span>}
                                    {p.label}
                                    {i < log.path.length - 1 && <span className="text-gray-300">&gt;</span>}
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                          <div className="w-80 flex gap-4 justify-end">
                            {log.status.length === 2 && (
                              <>
                                <span className={`px-6 py-2 rounded-lg text-white text-base font-semibold ${leftStatus.color}`}>{leftStatus.label}</span>
                                <span className={`px-6 py-2 rounded-lg text-white text-base font-semibold ${rightStatus.color}`}>{rightStatus.label}</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SidebarItem({ icon, label, open, active }) {
  return (
    <div className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${active ? "bg-blue-100 dark:bg-blue-900 font-semibold" : ""}`}>{icon}{open && <span>{label}</span>}</div>
  );
}

function Avatar({ initials }) {
  if (typeof initials === 'object') return initials;
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 font-bold text-sm text-gray-700 dark:text-gray-100">{initials}</span>;
}

